<template>
  <div :style="style">
    <audio
      v-if="value.data.audio"
      :id="playerId"
      :ref="playerId"
      width="100%"
      :src="value.data.audio.url"
      class="p-block-audio-viewer vjs-big-play-centered video-js">
      <template v-for="(track, key) in videoSubtitles">
        <track v-if="track" :key="key" :label="subtitlesLang[key]" kind="captions" :srclang="key" :src="track.url"/>
      </template>
    </audio>
  </div>
</template>

<script>
import videojs from "video.js"
import "video.js/dist/video-js.css"
import { availableLanguages } from "../video/availableLanguages.js"
import { RevisionDisplayMode } from "plant-common/src/models/revisionDisplayMode.js";
import { isPlayerProgressBarBlocked } from "plant-common/src/utils/mediaPlayer.js";

export default {
  props: ["value", "mode"],
  inject: ["lessonPlanController"],
  data() {
    return {
      player: null,
      subtitlesLang: availableLanguages,
      audioTrackCueChangeHandler: null
    }
  },
  computed: {
    style() {
      return {
        overflow: "hidden",
        "--audio-video-controls-color": this.value.data.controls.color,
        "--audio-video-controls-size": `${this.value.data.controls.size}px`,
        "--audio-video-controls-button-zoom": this.value.data.controls.size / 25
      }
    },
    options() {
      return {
        controls: true,
        width: this.value.size.width,
        height: this.value.size.height,
        controlBar: {
          pictureInPictureToggle: false,
          volumePanel: false,
          fullscreenToggle: false
        },
        sources: [
          {
            src: this.value.data.audio ? this.value.data.audio.url : null,
            type: this.value.data.audio ? this.value.data.audio.type : "audio/mp3"
          }
        ]
      }
    },
    playerId() {
      return `audioPlayer-${this.value.id}`
    },
    videoSubtitles() {
      return this.value.data.subtitles
    },
    isSubtitlesActive() {
      return this.lessonPlanController.subtitlesActive
    },
		isBlockVisited() {
			return this.value.data.visited;
		},
		isEditMode() {
			return this.lessonPlanController.mode === RevisionDisplayMode.edit;
		},
    parentSubcanvasDelay() {
			return this.$parent.$parent.$attrs.subcanvasDelay ? this.$parent.$parent.$attrs.subcanvasDelay : 0
		}
  },
  mounted() {
    const ref = this.$refs[this.playerId]

    if (ref) this.initAudioPlayer(ref)

    const hasAudioSource = Boolean(this.value.data.audio)

    if (!this.isEditMode && !hasAudioSource) this.$set(this.value.data, 'visited', true)
  },
  beforeDestroy() {
    if (!this.player) return;

    const textTracks = this.player.textTracks();
    const tracks = textTracks && textTracks.tracks_;

    if (tracks) {
      this.resetTracks(tracks);

      this.audioTrackCueChangeHandler = null;
    }

    this.player.dispose();
  },
  methods: {
    initAudioPlayer(ref) {
      const context = this

      this.player = videojs(ref, this.options, function onPlayerReady() {
        if (context.value.data.autoplay && context.mode == "view") {
          setTimeout(() => {
            context.player.play()
          }, (context.value.data.delay + context.parentSubcanvasDelay) * 1000)
        }

        if (isPlayerProgressBarBlocked(context.lessonPlanController, context.isBlockVisited)) {
          this.controlBar.progressControl.disable()
        }
      })

      this.player.on("click", (event) => {
        event.stopPropagation()
      })

      this.player.on("play", () => {
        if(this.isSubtitlesActive) {
          this.turnOnSubtitles()
        }
      })
        
      this.player.on("ended", () => {
        if (!this.isEditMode) {
          this.$set(this.value.data, 'visited', true);
        }

        this.player.controlBar.progressControl.enable()
      })

      this.changePlayerUI(this.value.data.simpleUI)
      this.$el.querySelector(".vjs-big-play-button").title = "Play Audio"
    },
    changePlayerUI(simple) {
      this.$el.querySelector(".vjs-progress-control").style.display = simple ? "none" : ""
      this.$el.querySelector(".vjs-remaining-time").style.display = simple ? "none" : ""
      this.$el.querySelector(".vjs-control-bar").style.display = simple ? "block" : ""
      this.$el.querySelector(".vjs-big-play-button").style.display = simple ? "none" : ""
    },
    turnOnSubtitles() {
      const textTracks = this.player.textTracks();
      const tracks = textTracks && textTracks.tracks_;

      if (!tracks) return;

      this.resetTracks(tracks);

      const defaultTrack = this.getDefaultTrack(tracks);

      if (!defaultTrack) return;

      defaultTrack.mode = 'showing';

      this.updateTextSubtitles(defaultTrack);

      if (!this.audioTrackCueChangeHandler) {
        this.audioTrackCueChangeHandler = () => this.updateTextSubtitles(defaultTrack);
      }

      defaultTrack.addEventListener('cuechange', this.audioTrackCueChangeHandler);
    },
    resetTracks(tracks) {
      tracks.forEach(track => {
        track.mode = 'hidden';

        if (this.audioTrackCueChangeHandler) {
          track.removeEventListener('cuechange', this.audioTrackCueChangeHandler);
        }
      });
    },
    getDefaultTrack(tracks) {
      const selectedLanguage = this.lessonPlanController.defaultSubtitles;

      if (selectedLanguage) {
        const selectedLanguageTrack = tracks.find(track => track.language === selectedLanguage)
        return selectedLanguageTrack ?? null;
      }

      return tracks[0] ?? null;
    },
    updateTextSubtitles(track) {
      const cues = track.activeCues;

      if (cues && cues.length > 0) {
        this.lessonPlanController.setSubtitlesText(cues[0].text);
      } else {
        this.lessonPlanController.setSubtitlesText(false);
      }
    }
  },
  watch: {
    "value.data.simpleUI": {
      handler() {
        this.changePlayerUI(this.value.data.simpleUI)
      }
    },
    isSubtitlesActive: {
      handler() {
        if(this.isSubtitlesActive) {
          this.turnOnSubtitles()
        }
      }
    },
    "lessonPlanController.defaultSubtitles": {
      handler() {
        this.turnOnSubtitles()
      }
    },
    "lessonPlanController.mode": {
      handler() {
        if (!this.player) return

        if (isPlayerProgressBarBlocked(this.lessonPlanController, this.isBlockVisited)) {
          this.player.controlBar.progressControl.disable()
          return
        }

        this.player.controlBar.progressControl.enable()
      }
    }
  }
}
</script>

<style>
.p-block-audio-viewer.video-js {
  color: var(--audio-video-controls-color);
}

/* Change the border of the big play button. */
.p-block-audio-viewer .vjs-big-play-button {
  border-color: var(--audio-video-controls-color);
  zoom: var(--audio-video-controls-button-zoom);
}

/* Change the color of various "bars". */
.p-block-audio-viewer .vjs-volume-level,
.p-block-audio-viewer .vjs-play-progress,
.p-block-audio-viewer .vjs-slider-bar {
  background: var(--audio-video-controls-color);
}
.p-block-audio-viewer .vjs-control-bar {
  font-size: var(--audio-video-controls-size);
  background-color: rgba(43, 51, 63, 1);
  height: 100%;
}

.p-block-audio-viewer .vjs-subs-caps-button {
  display: none;
}

.video-js .vjs-play-progress:before {
  top: 0em;
}
</style>